import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loading, MediaPicker } from "../../components";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import JoditEditor from "jodit-react";
import "jodit/es2021/jodit.min.css";
import {
  UploadSimple, Trash, TextT, LinkSimple, FolderSimple, X,
  Eye, Code, TextAlignLeft, CaretDown, Image as ImageIcon, Images,
} from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Textarea, Select, Switch, SeoFields,
  Card, CardHeader, CardTitle, CardBody, cn,
} from "../../components/ui";

const FRONTEND = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";

const escAttr = (s) =>
  String(s || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Jodit's "empty" value is `<p><br></p>` — treat markup-only content as empty. */
const isEmptyHtml = (html) => !String(html || "").replace(/<[^>]*>|&nbsp;|\s/g, "");

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "scheduled", label: "Scheduled" },
];

function FormBlogs() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { checkAccess } = useAccessChecker();
  const canEditHtml = checkAccess("blogs", "update") || checkAccess("blogs", "create");

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [editorMode, setEditorMode] = useState("visual"); // visual | html | preview
  const [seoAdvanced, setSeoAdvanced] = useState(false);
  const [picker, setPicker] = useState(null); // null | "editor" | "featured" | "og"
  const joditRef = useRef(null);
  // Latest editor content without re-rendering the whole form on each keystroke.
  const contentRef = useRef("");

  const [formData, setFormData] = useState({
    image: null,
    image_alt: "",
    image_caption: "",
    title: "",
    slug: "",
    description: "",
    meta_title: "",
    meta_description: "",
    canonical_url: "",
    noindex: false,
    og_title: "",
    og_description: "",
    og_image: null,
    content: "",
    category_id: "",
    status: "published",
    published_at: "",
  });

  useEffect(() => {
    getCategory();
    if (id) getBlogToUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBlogToUpdate = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/blogs/get/${id}`);
      const b = response.data.data || {};
      contentRef.current = b.content || "";
      setFormData((prev) => ({
        ...prev,
        ...b,
        noindex: Boolean(b.noindex),
        status: b.status || "published",
        published_at: toLocalInput(b.published_at),
        // keep controlled inputs as strings
        image_alt: b.image_alt || "",
        image_caption: b.image_caption || "",
        meta_title: b.meta_title || "",
        meta_description: b.meta_description || "",
        canonical_url: b.canonical_url || "",
        og_title: b.og_title || "",
        og_description: b.og_description || "",
      }));
    } catch (error) {
      console.error("Failed to fetch blog:", error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // Blog categories are managed separately from product categories.
  const getCategory = async () => {
    try {
      const response = await axiosInstance.get(`/blog-categories`);
      if (response.data) setCategories(response.data.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load categories");
    }
  };

  const [catModal, setCatModal] = useState(false);
  const [catName, setCatName] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [catSaving, setCatSaving] = useState(false);

  const submitCategory = async () => {
    if (!catName.trim()) return toast.warning("Le nom est requis");
    setCatSaving(true);
    try {
      const response = await axiosInstance.post(`/blog-categories`, { name: catName.trim(), description: catDesc.trim() || null });
      const created = response.data?.data;
      await getCategory();
      if (created?.id) setFormData((p) => ({ ...p, category_id: created.id }));
      toast.success("Catégorie créée");
      setCatModal(false);
      setCatName("");
      setCatDesc("");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create category");
    } finally {
      setCatSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const readImageFile = (file, key) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.warning("File size exceeds 15 MB limit");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setFormData((prev) => ({ ...prev, [key]: reader.result }));
  };

  const handleImageChange = (e) => readImageFile(e.target.files?.[0], "image");
  const handleOgImageChange = (e) => readImageFile(e.target.files?.[0], "og_image");

  /*
   * Jodit editor configuration. The built-in image/file dialogs are replaced
   * by the shared MediaPicker (persistent modal, media library + upload), so
   * every inserted image lives in the library and carries alt/caption/title.
   * Pasted images are NOT embedded as base64 — the server strips data: URIs.
   */
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      height: 560,
      minHeight: 420,
      language: "fr",
      toolbarAdaptive: false,
      statusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: "insert_clear_html",
      uploader: { insertImageAsBase64URI: false },
      disablePlugins: "file,video,print,about,classSpan,speechRecognize,ai-assistant",
      placeholder: "Rédigez votre article…",
      buttons: [
        "paragraph", "|",
        "bold", "italic", "underline", "strikethrough", "|",
        "ul", "ol", "|",
        "blockquote", "hr", "table", "|",
        "link",
        {
          name: "image",
          tooltip: "Insérer une image (médiathèque)",
          exec: () => setPicker("editor"),
        },
        "|", "left", "center", "right", "|",
        "undo", "redo", "|",
        "eraser", "fullsize",
      ],
    }),
    []
  );

  // Switch Visual / HTML / Preview — committing the live editor content first
  // so the other modes (and submit) always see the latest text.
  const switchMode = (mode) => {
    if (editorMode === "visual") {
      setFormData((p) => ({ ...p, content: contentRef.current ?? p.content }));
    }
    setEditorMode(mode);
  };

  const currentContent = () =>
    editorMode === "visual" ? (contentRef.current ?? formData.content) : formData.content;

  // Insert an image chosen in the MediaPicker at the cursor position.
  const insertEditorImage = ({ url, alt, caption, title }) => {
    const img = `<img src="${escAttr(url)}" alt="${escAttr(alt)}"${title ? ` title="${escAttr(title)}"` : ""}>`;
    const html = caption
      ? `<figure>${img}<figcaption>${escAttr(caption)}</figcaption></figure><p><br></p>`
      : `<p>${img}</p>`;
    const ed = joditRef.current;
    if (editorMode === "visual" && ed?.s) {
      ed.s.focus();
      ed.s.insertHTML(html);
      contentRef.current = ed.value;
      setFormData((p) => ({ ...p, content: ed.value }));
    } else {
      contentRef.current = (currentContent() || "") + html;
      setFormData((p) => ({ ...p, content: contentRef.current }));
    }
  };

  const handlePickerSelect = (sel) => {
    if (picker === "editor") {
      insertEditorImage(sel);
    } else if (picker === "featured") {
      setFormData((p) => ({
        ...p,
        image: sel.url,
        image_alt: sel.alt || p.image_alt,
        image_caption: sel.caption || p.image_caption,
      }));
    } else if (picker === "og") {
      setFormData((p) => ({ ...p, og_image: sel.url }));
    }
  };

  const validateForm = () => {
    const formErrors = {};
    if (!formData.title) formErrors.title = "Title is required";
    if (!formData.slug) formErrors.slug = "Slug is required";
    if (!formData.category_id) formErrors.category_id = "Category is required";
    if (isEmptyHtml(currentContent())) formErrors.content = "Content is required";
    if (formData.status === "scheduled") {
      if (!formData.published_at) formErrors.published_at = "A future publish date is required";
      else if (new Date(formData.published_at).getTime() <= Date.now())
        formErrors.published_at = "The publish date must be in the future";
    }
    setErrors(formErrors);
    if (Object.keys(formErrors).length) toast.warning("Please fix the highlighted fields");
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const payload = { ...formData, content: currentContent() };
      const response = id
        ? await axiosInstance.put(`/blogs/update/${id}`, payload)
        : await axiosInstance.post("/blogs/create", payload);
      toast.success(response.data.message);
      navigate("/blogs");
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while processing the request");
    } finally {
      setLoading(false);
    }
  };

  // Open a signed, non-indexed preview of the current (saved) article on the storefront.
  const handlePreview = async () => {
    if (!id) return toast.info("Save the post first to preview it.");
    try {
      const { data } = await axiosInstance.get(`/blogs/preview-link/${id}`);
      window.open(`${FRONTEND}/preview/post/${id}?${data.query}`, "_blank", "noopener");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not generate preview link");
    }
  };

  const categoryOptions = (categories || []).map((c) => ({ value: c.id, label: c.name }));

  const EditorTab = ({ mode, icon: Icon, children }) => (
    <button
      type="button"
      onClick={() => switchMode(mode)}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition",
        editorMode === mode ? "bg-accent text-white" : "text-muted hover:bg-surface-2 hover:text-ink"
      )}
    >
      <Icon size={15} /> {children}
    </button>
  );

  return (
    <div className="w-full p-6 text-start">
      {loading && <Loading />}

      <PageHeader
        title={id ? "Edit post" : "New post"}
        subtitle="Write and publish an article."
        breadcrumbs={[{ label: "Blogs", to: "/blogs" }, { label: id ? "Edit" : "New" }]}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate("/blogs")}>
              Cancel
            </Button>
            {id && (
              <Button type="button" variant="secondary" icon={Eye} onClick={handlePreview}>
                Preview
              </Button>
            )}
            <Button type="submit" form="blog-form" loading={loading}>
              {id ? "Update post" : "Publish post"}
            </Button>
          </>
        }
      />

      <form id="blog-form" onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column — Content */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Content</CardTitle>
            </CardHeader>
            <CardBody className="space-y-5">
              <Input
                label="Title"
                icon={TextT}
                name="title"
                value={formData.title || ""}
                onChange={handleChange}
                placeholder="Enter post title"
                error={errors.title}
              />
              <Input
                label="Slug"
                icon={LinkSimple}
                name="slug"
                value={formData.slug || ""}
                onChange={handleChange}
                placeholder="enter-post-slug"
                error={errors.slug}
              />
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-sm font-medium text-ink/90">Content</label>
                  <div className="flex items-center gap-1 rounded-xl border border-line bg-surface p-1">
                    <EditorTab mode="visual" icon={TextAlignLeft}>Visual</EditorTab>
                    {canEditHtml && <EditorTab mode="html" icon={Code}>HTML</EditorTab>}
                    <EditorTab mode="preview" icon={Eye}>Preview</EditorTab>
                  </div>
                </div>

                {editorMode === "visual" && (
                  <div className="overflow-hidden rounded-xl border border-line [&_.jodit-container]:!border-0">
                    <JoditEditor
                      ref={joditRef}
                      value={formData.content || ""}
                      config={joditConfig}
                      onChange={(content) => { contentRef.current = content; }}
                      onBlur={(content) => {
                        contentRef.current = content;
                        setFormData((p) => ({ ...p, content }));
                      }}
                    />
                  </div>
                )}

                {editorMode === "html" && canEditHtml && (
                  <>
                    <Textarea
                      value={formData.content || ""}
                      onChange={(e) => {
                        contentRef.current = e.target.value;
                        setFormData((p) => ({ ...p, content: e.target.value }));
                      }}
                      rows={24}
                      className="font-mono text-xs"
                      placeholder="<p>Raw HTML — sanitised on save.</p>"
                    />
                    <p className="mt-1 text-xs text-muted">
                      HTML is sanitised on the server (scripts and disallowed tags are stripped) before saving.
                    </p>
                  </>
                )}

                {editorMode === "preview" && (
                  <div className="rounded-xl border border-line bg-surface p-5">
                    <div className="blog-html-preview" dangerouslySetInnerHTML={{ __html: formData.content || "<p class='text-muted'>Nothing to preview yet.</p>" }} />
                  </div>
                )}

                {errors.content && <p className="mt-2 text-xs text-red-500">{errors.content}</p>}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          {/* Publishing */}
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
            </CardHeader>
            <CardBody className="space-y-5">
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData.status}
                onChange={(v) => setFormData((p) => ({ ...p, status: v || "published" }))}
              />
              {formData.status !== "draft" && (
                <Input
                  type="datetime-local"
                  label={formData.status === "scheduled" ? "Publish at (scheduled)" : "Publication date"}
                  name="published_at"
                  value={formData.published_at || ""}
                  onChange={handleChange}
                  error={errors.published_at}
                  hint={formData.status === "scheduled" ? "The article goes live automatically at this time." : "Leave blank to publish now."}
                />
              )}
              <Select
                label="Category"
                icon={FolderSimple}
                searchable
                placeholder="Select a category"
                options={categoryOptions}
                value={formData.category_id || null}
                onChange={(v) => setFormData((p) => ({ ...p, category_id: v || "" }))}
              />
              {errors.category_id && <p className="-mt-3 text-xs text-red-500">{errors.category_id}</p>}
              <button type="button" onClick={() => setCatModal(true)} className="-mt-2 self-start text-xs font-medium text-accent transition hover:underline">
                + Nouvelle catégorie d'article
              </button>
            </CardBody>
          </Card>

          {/* Media */}
          <Card>
            <CardHeader>
              <CardTitle>Media</CardTitle>
            </CardHeader>
            <CardBody className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink/90">Featured image</label>
                {formData.image ? (
                  <div className="relative overflow-hidden rounded-xl border border-line">
                    <img src={formData.image} alt="" className="max-h-56 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, image: null }))}
                      className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-lg bg-black/60 text-white transition hover:bg-black/80"
                      title="Remove"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface-2 px-6 py-10 text-center transition hover:border-accent/50 hover:bg-surface"
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-faint">
                      <UploadSimple size={20} />
                    </span>
                    <span className="text-sm font-medium text-ink">Upload image</span>
                    <span className="text-xs text-muted">JPG, PNG or GIF · max 15MB</span>
                    <input id="image-upload" type="file" className="hidden" accept="image/*" onChange={handleImageChange} name="image" />
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setPicker("featured")}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent transition hover:underline"
                >
                  <Images size={14} /> Choisir depuis la médiathèque
                </button>
                {formData.image && (
                  <>
                    <Input
                      containerClassName="mt-3"
                      label="Image alt text"
                      name="image_alt"
                      value={formData.image_alt || ""}
                      onChange={handleChange}
                      placeholder="Describe the image for SEO & accessibility"
                    />
                    <Input
                      containerClassName="mt-3"
                      label="Caption (optional)"
                      name="image_caption"
                      value={formData.image_caption || ""}
                      onChange={handleChange}
                      placeholder="Shown under the image"
                    />
                  </>
                )}
              </div>

              <Textarea
                label="Short description / excerpt"
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                placeholder="A short summary shown in listings and used as a meta/OG fallback"
              />
            </CardBody>
          </Card>

          {/* SEO */}
          <SeoFields
            value={{ slug: formData.slug, meta_title: formData.meta_title, meta_description: formData.meta_description }}
            onChange={(field, val) => setFormData((p) => ({ ...p, [field]: val }))}
            fallbackTitle={formData.title}
            fallbackDescription={formData.description}
            basePath="/blog/"
            showSlug={false}
          />

          {/* SEO — advanced / social (collapsible) */}
          <Card>
            <button
              type="button"
              onClick={() => setSeoAdvanced((v) => !v)}
              className="flex w-full items-center justify-between gap-3 border-b border-line px-5 py-4 text-left"
            >
              <span className="text-sm font-semibold text-ink">Social & advanced SEO</span>
              <CaretDown size={16} className={cn("text-muted transition-transform", seoAdvanced && "rotate-180")} />
            </button>
            {seoAdvanced && (
              <CardBody className="space-y-5">
                <Input
                  label="Canonical URL (optional)"
                  icon={LinkSimple}
                  name="canonical_url"
                  value={formData.canonical_url || ""}
                  onChange={handleChange}
                  placeholder="https://www.technifro.ma/post/…"
                  hint="Leave blank to use this article's own URL."
                />
                <div className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-2 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium text-ink">Hide from search engines</div>
                    <div className="text-xs text-muted">Adds noindex, nofollow to this article.</div>
                  </div>
                  <Switch checked={!!formData.noindex} onChange={(v) => setFormData((p) => ({ ...p, noindex: v }))} />
                </div>
                <Input
                  label="Open Graph title (optional)"
                  name="og_title"
                  value={formData.og_title || ""}
                  onChange={handleChange}
                  placeholder={formData.meta_title || formData.title || "Falls back to the SEO/article title"}
                />
                <Textarea
                  label="Open Graph description (optional)"
                  name="og_description"
                  value={formData.og_description || ""}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Falls back to the meta description / excerpt"
                />
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink/90">Open Graph image (optional)</label>
                  {formData.og_image ? (
                    <div className="relative overflow-hidden rounded-xl border border-line">
                      <img src={formData.og_image} alt="" className="max-h-40 w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, og_image: null }))}
                        className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-lg bg-black/60 text-white transition hover:bg-black/80"
                        title="Remove"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="og-image-upload"
                      className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface-2 px-4 py-6 text-center text-sm text-muted transition hover:border-accent/50 hover:bg-surface"
                    >
                      <ImageIcon size={18} /> Upload social share image
                      <input id="og-image-upload" type="file" className="hidden" accept="image/*" onChange={handleOgImageChange} />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => setPicker("og")}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent transition hover:underline"
                  >
                    <Images size={14} /> Choisir depuis la médiathèque
                  </button>
                  <p className="mt-1 text-xs text-muted">Falls back to the featured image when empty.</p>
                </div>
              </CardBody>
            )}
          </Card>
        </div>
      </form>

      {/* Scoped styling for the in-editor HTML preview */}
      <style>{`
        .blog-html-preview { color: #3f4147; font-size: 14px; line-height: 1.7; }
        .blog-html-preview h2, .blog-html-preview h3, .blog-html-preview h4 { font-weight: 600; color: #16181d; margin: 1em 0 .4em; }
        .blog-html-preview p { margin: .6em 0; }
        .blog-html-preview a { color: #3270c0; text-decoration: underline; }
        .blog-html-preview ul { list-style: disc; padding-left: 1.25rem; }
        .blog-html-preview ol { list-style: decimal; padding-left: 1.25rem; }
        .blog-html-preview img { max-width: 100%; height: auto; border-radius: 8px; margin: .8em 0; }
        .blog-html-preview figure { margin: 1em 0; }
        .blog-html-preview figcaption { text-align: center; font-size: 12px; color: #86888f; margin-top: .4em; }
        .blog-html-preview blockquote { border-left: 3px solid #d8dade; padding-left: 1rem; color: #6b6d74; }
      `}</style>

      {/* Shared media library picker (editor inserts, featured & OG images) */}
      <MediaPicker
        open={picker !== null}
        onClose={() => setPicker(null)}
        onSelect={handlePickerSelect}
        withMeta={picker !== "og"}
        insertLabel={picker === "editor" ? "Insérer" : "Utiliser"}
      />

      {/* New blog-category modal */}
      {createPortal(
        <AnimatePresence>
          {catModal ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              onClick={() => !catSaving && setCatModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.97 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md rounded-2xl border border-line bg-surface p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-ink">Nouvelle catégorie d'article</h3>
                  <button type="button" onClick={() => setCatModal(false)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Fermer">
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Nom"
                    value={catName}
                    onChange={(e) => setCatName(e.target.value)}
                    placeholder="Ex. Guides d'achat"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submitCategory())}
                  />
                  <Textarea
                    label="Description (facultatif)"
                    rows={2}
                    value={catDesc}
                    onChange={(e) => setCatDesc(e.target.value)}
                    placeholder="Courte description de la catégorie"
                  />
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={() => setCatModal(false)} disabled={catSaving}>
                    Annuler
                  </Button>
                  <Button type="button" onClick={submitCategory} loading={catSaving}>
                    Créer la catégorie
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

export default FormBlogs;
